import React, { useEffect, useMemo, useState } from 'react';
import { Cpu } from 'lucide-react';
import { llmGatewayApi } from '../../llm-gateway/llmGatewayApi';
import type { LlmGateway } from '../../llm-gateway/llmGatewayTypes';
import { FormSection, FormStack, GatewayDrawer, SearchableSelect, fieldClass, ghostBtn, helperClass, labelClass, primaryBtn, selectField } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { QuotaAssignmentStatus, QuotaListingTab, QuotaScope } from '../governedMcpTypes';
import { FOREVER_MONTH, currentQuotaMonth, formatQuotaMonth, isQuotaMonth } from '../quotaUsage';

const SCOPE_RADIOS: { id: QuotaScope; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'user', label: 'User' },
  { id: 'role', label: 'Role' },
  { id: 'department', label: 'Department' },
];

function seedFormScope(listing: QuotaListingTab): QuotaScope {
  if (listing === 'user' || listing === 'role' || listing === 'department') return listing;
  return 'all';
}

function parseLimit(value: string): number {
  return Number(value.replace(/[$,]/g, '').trim());
}

export default function LlmQuotaAssignDrawer({
  open,
  listing,
  clientId,
  month,
  isLight,
  onClose,
  onAssigned,
  onToast,
}: {
  open: boolean;
  listing: QuotaListingTab;
  clientId: string;
  month: string;
  isLight: boolean;
  onClose: () => void;
  onAssigned?: (next: { listing: QuotaListingTab; month: string }) => void;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, quotaDirectory, setQuotaAssignment } = useGovernedMcp();
  const [formScope, setFormScope] = useState<QuotaScope>('all');
  const [formClientId, setFormClientId] = useState('');
  const [gatewayId, setGatewayId] = useState('');
  const [scopeId, setScopeId] = useState('');
  const [scopeIds, setScopeIds] = useState<string[]>([]);
  const [unlimited, setUnlimited] = useState(false);
  const [formMonth, setFormMonth] = useState(currentQuotaMonth());
  const [tokenLimit, setTokenLimit] = useState('');
  const [status, setStatus] = useState<QuotaAssignmentStatus>('active');
  const [renewsMonthly, setRenewsMonthly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gateways, setGateways] = useState<LlmGateway[]>([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [gatewaysError, setGatewaysError] = useState<string | null>(null);

  const directoryUsers = useMemo(
    () =>
      quotaDirectory.users.map((item) => ({
        id: item.id,
        label: item.name,
        hint: item.email || item.roleName || '',
      })),
    [quotaDirectory.users]
  );

  const options: { id: string; label: string; hint?: string }[] =
    formScope === 'department'
      ? quotaDirectory.departments.map((item) => ({ id: item.id, label: item.name }))
      : formScope === 'role'
        ? quotaDirectory.roles.map((item) => ({ id: item.id, label: item.name }))
        : directoryUsers;

  const gatewayOptions = useMemo(
    () =>
      gateways.map((item) => ({
        id: item.id,
        label: item.name || item.id,
        hint: [item.publicAlias, item.llmType, item.status].filter(Boolean).join(' · '),
      })),
    [gateways]
  );

  useEffect(() => {
    if (!open) return;
    setFormScope(seedFormScope(listing));
    setFormClientId(clientId !== 'all' ? clientId : state.clients[0]?.id || '');
    setGatewayId('');
    setScopeId('');
    setScopeIds([]);
    setUnlimited(false);
    setFormMonth(isQuotaMonth(month) ? month : currentQuotaMonth());
    setTokenLimit('');
    setStatus('active');
    setRenewsMonthly(false);
    setError(null);
  }, [clientId, listing, month, open, state.clients]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setGateways([]);
    setGatewaysError(null);
    setGatewaysLoading(true);
    logGovernedMcp('app', 'llm_quota_gateways_load');
    llmGatewayApi
      .listGateways()
      .then((items) => {
        if (cancelled) return;
        setGateways(items);
        logGovernedMcp('app', 'llm_quota_gateways_loaded', { count: items.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load LLM gateways.';
        setGatewaysError(message);
        logGovernedMcp('error', 'llm_quota_gateways_failed', { message });
      })
      .finally(() => {
        if (!cancelled) setGatewaysLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const persistClientId = formClientId || (clientId !== 'all' ? clientId : '');
  const selected = options.find((item) => item.id === scopeId);

  const fail = (reason: string) => {
    setError(reason);
    logGovernedMcp('warning', 'llm_quota_assign_invalid', { reason });
    onToast('error', reason);
  };

  const targets = (): { id: string; label: string }[] => {
    if (formScope === 'all') return [{ id: 'all', label: 'All' }];
    if (formScope === 'user') {
      const picked = scopeIds.length ? scopeIds : scopeId ? [scopeId] : [];
      return options.filter((item) => picked.includes(item.id)).map((item) => ({ id: item.id, label: item.label }));
    }
    return selected ? [{ id: selected.id, label: selected.label }] : [];
  };

  const finishLabel = (count: number, gatewayLabel?: string) => {
    const suffix = gatewayLabel ? ` · ${gatewayLabel}` : '';
    if (formScope === 'all') return `all${suffix}`;
    if (formScope === 'user' && count > 1) return `${count} users${suffix}`;
    return `${selected?.label || formScope}${suffix}`;
  };

  const submit = () => {
    if (!persistClientId.trim()) {
      fail('Select a registered AI client.');
      return;
    }
    if (!gatewayId.trim()) {
      fail('Select an LLM gateway.');
      return;
    }
    const nextTargets = targets();
    if (!nextTargets.length) {
      fail(formScope === 'user' ? 'Select at least one user.' : `Select a ${formScope}.`);
      return;
    }
    if (!unlimited) {
      if (!isQuotaMonth(formMonth)) {
        fail('Select a month.');
        return;
      }
      const parsed = parseLimit(tokenLimit);
      if (!Number.isFinite(parsed) || parsed < 0) {
        fail('Monthly token limit must be zero or a positive number.');
        return;
      }
    }
    const gateway = gatewayOptions.find((item) => item.id === gatewayId);
    const parsed = unlimited ? 0 : parseLimit(tokenLimit);
    const period = unlimited ? FOREVER_MONTH : formMonth;
    for (const target of nextTargets) {
      const saved = setQuotaAssignment(
        formScope,
        target.id,
        persistClientId,
        parsed,
        gateway?.label || target.label,
        period,
        status,
        unlimited ? false : renewsMonthly,
        'llm_gateway',
        gatewayId
      );
      if (!saved.ok) {
        fail(saved.reason);
        return;
      }
    }
    logGovernedMcp('app', 'llm_quota_assignment_saved', {
      scope: formScope,
      count: nextTargets.length,
      unlimited,
      clientId: persistClientId,
      gatewayId,
      month: period,
      tokenLimit: parsed,
    });
    onAssigned?.({ listing: formScope === 'all' ? 'all' : formScope, month: period });
    onToast('success', `Quota assigned for ${finishLabel(nextTargets.length, gateway?.label)}.`);
    onClose();
  };

  const toggleUser = (id: string) => {
    setScopeIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
    setScopeId(id);
  };

  return (
    <GatewayDrawer
      open={open}
      title="Assign LLM quota"
      subtitle={formScope === 'all' ? 'All' : formScope}
      eyebrow="Governed MCP / Quotas & Spendings"
      icon={<Cpu size={18} />}
      isLight={isLight}
      onClose={onClose}
      size="lg"
      maximizable
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit}>
            Assign quota
          </button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}
      <FormStack>
        <FormSection title="Assignment" description="One policy for all, or a specific user, role, or department. Unlimited lasts forever." isLight={isLight}>
          <div className="space-y-4">
            <div>
              <label className={labelClass(isLight)}>Client</label>
              <select className={`${selectField(isLight)} w-full`} value={formClientId} onChange={(event) => setFormClientId(event.target.value)}>
                <option value="">Select a client</option>
                {state.clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            <SearchableSelect
              label="LLM gateway"
              value={gatewayId}
              options={gatewayOptions}
              onChange={setGatewayId}
              isLight={isLight}
              placeholder={gatewaysLoading ? 'Loading gateways…' : gatewayOptions.length ? 'Select an LLM gateway' : 'No LLM gateways'}
              searchPlaceholder="Search gateways…"
            />
            {gatewaysError ? <p className="text-xs text-red-500">{gatewaysError}</p> : null}

            <div>
              <label className={labelClass(isLight)}>Scope</label>
              <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Quota scope">
                {SCOPE_RADIOS.map((option) => {
                  const selectedScope = formScope === option.id;
                  return (
                    <label
                      key={option.id}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm cursor-pointer ${
                        selectedScope
                          ? isLight
                            ? 'border-indigo-400 bg-indigo-50/80 text-indigo-800'
                            : 'border-indigo-400/70 bg-indigo-500/15 text-indigo-200'
                          : isLight
                            ? 'border-neutral-200 bg-white text-neutral-700'
                            : 'border-neutral-800 bg-neutral-950 text-neutral-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="llm-quota-scope"
                        value={option.id}
                        checked={selectedScope}
                        onChange={() => {
                          setFormScope(option.id);
                          setScopeId('');
                          setScopeIds([]);
                          setError(null);
                        }}
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {formScope === 'all' ? (
              <p className={helperClass(isLight)}>One assignment covers every caller of this gateway.</p>
            ) : formScope === 'user' ? (
              <div>
                <p className={labelClass(isLight)}>Users</p>
                <div className={`max-h-48 overflow-auto rounded-xl border divide-y ${isLight ? 'border-neutral-200 divide-neutral-100' : 'border-neutral-800 divide-neutral-800'}`}>
                  {options.length ? (
                    options.map((item) => (
                      <label key={item.id} className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer">
                        <input type="checkbox" checked={scopeIds.includes(item.id)} onChange={() => toggleUser(item.id)} />
                        <span className="min-w-0">
                          <span className={`block truncate ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{item.label}</span>
                          {item.hint ? <span className={`block text-[11px] truncate ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{item.hint}</span> : null}
                        </span>
                      </label>
                    ))
                  ) : (
                    <p className={`px-3 py-3 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>No users in the directory.</p>
                  )}
                </div>
              </div>
            ) : (
              <SearchableSelect
                label={formScope === 'role' ? 'Role' : 'Department'}
                value={scopeId}
                options={options}
                onChange={setScopeId}
                isLight={isLight}
                placeholder={`Select a ${formScope}`}
              />
            )}

            <div>
              <label className={labelClass(isLight)}>Status</label>
              <select className={`${selectField(isLight)} w-full`} value={status} onChange={(event) => setStatus(event.target.value as QuotaAssignmentStatus)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <label className={`flex items-start gap-3 rounded-xl border px-3 py-3 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
              <input
                type="checkbox"
                className="mt-1"
                checked={unlimited}
                onChange={(event) => setUnlimited(event.target.checked)}
              />
              <span>
                <span className={`block text-sm font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>Unlimited</span>
                <span className={helperClass(isLight)}>No token cap, and no month. The policy lasts forever.</span>
              </span>
            </label>

            {unlimited ? null : (
              <>
                <div>
                  <label className={labelClass(isLight)}>Month</label>
                  <input type="month" className={fieldClass(isLight)} value={formMonth} onChange={(event) => setFormMonth(event.target.value)} />
                  <p className={helperClass(isLight)}>{formatQuotaMonth(formMonth)}</p>
                </div>
                <label className={`flex items-start gap-3 rounded-xl border px-3 py-3 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
                  <input type="checkbox" className="mt-1" checked={renewsMonthly} onChange={(event) => setRenewsMonthly(event.target.checked)} />
                  <span>
                    <span className={`block text-sm font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>Renews monthly</span>
                    <span className={helperClass(isLight)}>The limit resets at the start of each month until you replace it.</span>
                  </span>
                </label>
                <div>
                  <label className={labelClass(isLight)}>Monthly token limit</label>
                  <input
                    className={`${fieldClass(isLight)} tabular-nums`}
                    inputMode="numeric"
                    placeholder="500000"
                    value={tokenLimit}
                    onChange={(event) => setTokenLimit(event.target.value)}
                  />
                </div>
              </>
            )}
          </div>
        </FormSection>
      </FormStack>
    </GatewayDrawer>
  );
}
